/**
 * Ten languages, three of them right-to-left.
 *
 * In shared housing the resident is often not an English speaker, and this page
 * is the only interface they have to their own tenancy. Getting this wrong does
 * not degrade the experience — it removes it.
 *
 * Three decisions worth knowing:
 *
 * 1. The picker sits on the sign-in screen, *before* authentication. Somebody
 *    who cannot read that page cannot reach a settings menu to change it.
 *
 * 2. Direction is applied once, at the document level, when the language is
 *    chosen. Flipping a live layout is where RTL bugs come from; reloading into
 *    the right direction is unglamorous and correct.
 *
 * 3. English is built in; every other language is its own small file
 *    (src/lib/lang/), fetched when it is chosen. A resident on mobile data
 *    downloads the page and their language, not all ten.
 */

export type Lang = 'en' | 'ar' | 'ur' | 'hi' | 'ru' | 'fa' | 'zh' | 'es' | 'fr' | 'fil';

export interface LangMeta {
  code: Lang;
  /** In its own script, because that is what a speaker recognises. */
  label: string;
  english: string;
  dir: 'ltr' | 'rtl';
  font: string;
}

export const LANGUAGES: LangMeta[] = [
  { code: 'en', label: 'English', english: 'English', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
  { code: 'ar', label: 'العربية', english: 'Arabic', dir: 'rtl', font: "'Noto Sans Arabic', sans-serif" },
  { code: 'ur', label: 'اردو', english: 'Urdu', dir: 'rtl', font: "'Noto Sans Arabic', sans-serif" },
  { code: 'hi', label: 'हिन्दी', english: 'Hindi', dir: 'ltr', font: "'Noto Sans Devanagari', sans-serif" },
  { code: 'ru', label: 'Русский', english: 'Russian', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
  { code: 'fa', label: 'فارسی', english: 'Persian', dir: 'rtl', font: "'Noto Sans Arabic', sans-serif" },
  // the phone's own Chinese font: a web font for Chinese is megabytes
  { code: 'zh', label: '中文', english: 'Chinese', dir: 'ltr', font: "'PingFang SC', 'Microsoft YaHei', 'Noto Sans SC', sans-serif" },
  { code: 'es', label: 'Español', english: 'Spanish', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
  { code: 'fr', label: 'Français', english: 'French', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
  { code: 'fil', label: 'Filipino', english: 'Filipino', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
];

export const meta = (lang: Lang): LangMeta =>
  LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0]!;

/**
 * English is the reference. `scripts/check-i18n.mjs` fails the build if any
 * other language is missing a key that exists here, because the fallback —
 * showing English to somebody who chose Urdu — is silent and looks fine to
 * whoever wrote the code.
 */
const en = {
  'app.name': 'BurjManage',
  'app.tagline': 'Your tenancy',

  'signin.title': 'Sign in',
  'signin.intro': 'Use the email address your property office has for you.',
  'signin.email': 'Email',
  'signin.password': 'Password',
  'signin.submit': 'Sign in',
  'signin.checking': 'Checking…',
  'signin.or': 'or',
  'signin.forgot': 'Forgot your password?',
  'signin.firstTime': 'First time here?',
  'signin.create': 'Create your account',
  'signin.trouble': 'Need help? Call {phone}.',
  'signin.language': 'Language',

  'signup.title': 'Create your account',
  'signup.resetTitle': 'Set a new password',
  'signup.step': 'Step {n} of 3',
  'signup.intro': 'Enter the email address your property office has for you. We will email you a code.',
  'signup.sendEmail': 'Email me a code',
  'signup.sending': 'Sending…',
  'signup.emailCode': 'Code from the email',
  'signup.emailSent': 'We emailed a six-digit code to {email}. It expires in ten minutes. If you cannot see it, look in your spam or junk folder.',
  'signup.phoneTitle': 'Confirm your mobile number',
  'signup.phoneIntro': 'We will text a code to the number your office has for you: {phone}',
  'signup.sendSms': 'Text me a code',
  'signup.smsCode': 'Code from the text message',
  'signup.smsSent': 'We texted a six-digit code to {phone}. It expires in ten minutes.',
  'signup.wrongPhone': 'Not your number? Call the office to correct it.',
  'signup.passwordTitle': 'Choose a password',
  'signup.passwordIntro': 'At least {min} characters. You will sign in with your email and this password.',
  'signup.newPassword': 'Password',
  'signup.repeat': 'Type it again',
  'signup.mismatch': 'The two passwords are not the same.',
  'signup.save': 'Save and sign in',
  'signup.continue': 'Continue',
  'signup.checking': 'Checking…',
  'signup.resend': 'Send another code',
  'signup.confirmEmail': 'Your email address has changed. Confirm the new one to continue.',
  'signup.backToSignin': 'Back to sign in',
  'signup.show': 'Show',
  'signup.hide': 'Hide',

  'nav.home': 'Home',
  'nav.payments': 'Payments',
  'nav.report': 'Report',
  'nav.signout': 'Sign out',

  'home.greeting': 'Hello, {name}',
  'home.yourRoom': 'Your room',
  'home.rentDue': 'Rent due',
  'home.dueOn': 'Due {date}',
  'home.overdue': '{days} days late',
  'home.dueToday': 'Due today',
  'home.noRentDue': 'No rent is due right now.',
  'home.rentPaused': 'Your rent is paused while a repair is dealt with. You do not need to pay for now.',
  'home.monthlyRent': 'Monthly rent',
  'home.deposit': 'Deposit held',
  'home.tenancy': 'Tenancy',
  'home.until': 'Until {date}',
  'home.endingSoon': 'Your tenancy ends in {days} days. Someone will be in touch about renewing.',
  'home.howToPay': 'How to pay',
  'home.howToPayBody': 'Pay the office by transfer or in person. Once it is recorded you will see it under Payments, and you can show that screen as proof.',
  'pay.cta': 'I\'ve paid — tell the office',
  'pay.title': 'Tell us you have paid',
  'pay.intro': 'Nothing is charged here. The office checks your payment against the bank, then it appears under Payments.',
  'pay.how': 'How did you pay?',
  'pay.BankTransfer': 'Bank transfer',
  'pay.Cheque': 'Cheque',
  'pay.CreditCard': 'Card, at the office',
  'pay.Cash': 'Cash — please collect it',
  'pay.reference': 'Reference (optional)',
  'pay.referenceHint': 'The transfer or cheque number helps the office find it faster.',
  'pay.send': 'Send',
  'pay.sent': 'Thank you. The office will confirm it, usually within one working day.',
  'pay.cashSent': 'The office has been told. Someone will arrange to collect the cash.',
  'home.reported': 'You told us this is paid. The office is checking it.',
  'home.contact': 'Your contact',
  'home.callOffice': 'Call the office',
  'home.shared': 'Using a shared computer? Sign out when you are done.',

  'payments.title': 'Payments',
  'payments.none': 'No payments recorded yet.',
  'payments.received': 'Received {date}',
  'payments.method': 'Paid by {method}',
  'payments.proof': 'This is your record. It is written by the office when a payment is confirmed, and it cannot be edited afterwards.',
  'payments.kind.rent': 'Rent',
  'payments.kind.deposit': 'Deposit',
  'payments.kind.other': 'Other',

  'report.title': 'Report a problem',
  'report.intro': 'Tell us what is wrong. Someone will be assigned to it.',
  'report.category': 'What is the problem?',
  'report.describe': 'Describe it',
  'report.placeholder': 'Water coming through the bathroom ceiling since this morning',
  'report.send': 'Send',
  'report.sending': 'Sending…',
  'report.sent': 'Reported. Someone will be assigned and will contact you.',
  'report.emergency': 'This is an emergency',
  'report.emergencyHelp': 'Choose this only if the room is unsafe or unusable right now — flooding, no power, a broken lock, fire risk. It goes to someone immediately.',
  'report.emergencyOn': 'This will be treated as urgent and someone will be sent today.',
  'report.callInstead': 'If someone is in danger, call the emergency services first, then tell us.',

  'category.Plumbing': 'Water or plumbing',
  'category.Electrical': 'Electricity',
  'category.AirConditioning': 'Air conditioning',
  'category.Appliance': 'An appliance',
  'category.Internet': 'Internet',
  'category.Lock': 'Door or lock',
  'category.Cleaning': 'Cleanliness',
  'category.Pest': 'Pests',
  'category.Other': 'Something else',

  'error.network': 'We cannot reach the system. Check your connection and try again.',
  'error.badCode': 'That code is not right, or it has expired. Ask for another.',
  'error.noTenancy': 'We cannot find a tenancy for that email. Ask your property office to add your email address.',
  'error.badLogin': 'That email or password is not right.',
  'error.badEmail': 'That does not look like an email address.',
  'error.weakPassword': 'That password is too short.',
  'error.expired': 'This took too long. Please start again.',
  'error.googleFailed': 'Google sign-in did not work. Try again, or use your email and password.',
  'error.throttled': 'Too many tries. Wait 15 minutes, then ask for a new code.',
  'error.generic': 'Something went wrong. Please try again.',
  'error.retry': 'Try again',

  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.back': 'Back',
} as const;

export type Key = keyof typeof en;
export type Dict = Record<Key, string>;

/*
 * Each its own chunk: Vite splits a dynamic import into a file of its own. The
 * map is spelled out so the bundler can see every one.
 */
const LOADERS: Record<Exclude<Lang, 'en'>, () => Promise<{ default: Partial<Dict> }>> = {
  ar: () => import('./lang/ar'),
  ur: () => import('./lang/ur'),
  hi: () => import('./lang/hi'),
  ru: () => import('./lang/ru'),
  fa: () => import('./lang/fa'),
  zh: () => import('./lang/zh'),
  es: () => import('./lang/es'),
  fr: () => import('./lang/fr'),
  fil: () => import('./lang/fil'),
};

const loaded: Partial<Record<Lang, Partial<Dict>>> = { en };

/** The language's words, fetched once; English if the file cannot be had (offline). */
export async function loadLang(lang: Lang): Promise<void> {
  if (loaded[lang]) return;
  try { loaded[lang] = (await LOADERS[lang as Exclude<Lang, 'en'>]()).default; } catch { /* English stands in */ }
}
export const isLoaded = (lang: Lang): boolean => Boolean(loaded[lang]);

const STORAGE_KEY = 'burj.tenant.lang';

/**
 * Best guess from the browser, then whatever they last chose.
 *
 * A resident arriving from an SMS link should land in a language they can read
 * without touching anything, and never be moved out of one they picked.
 */
export function detectLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LANGUAGES.some((l) => l.code === saved)) return saved as Lang;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0];
    if (LANGUAGES.some((l) => l.code === base)) return base as Lang;
  }
  return 'en';
}

export function applyLang(lang: Lang): void {
  const m = meta(lang);
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = m.dir;
  document.documentElement.style.setProperty('--font', m.font);
}

/** `t('home.greeting', { name: 'Aisha' })` */
export function translator(lang: Lang) {
  const dict = loaded[lang] ?? en;
  return (key: Key, vars?: Record<string, string | number>): string => {
    // English is the fallback of last resort. It should never fire —
    // scripts/check-i18n.mjs fails the build if a key is missing anywhere.
    let out: string = dict[key] ?? en[key] ?? key;
    // split/join rather than replaceAll: replaceAll is ES2021 and this app is
    // built for ES2018 because residents open it on old, cheap phones. A method
    // a bundler cannot transpile would throw on exactly the devices that matter.
    if (vars) for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
    return out;
  };
}

export type T = ReturnType<typeof translator>;
